// src/pages/groups/MyStudents.jsx
// كل طلاب المدرس من كل مجموعاته (من غير تكرار) + المجموعات اللي كل طالب فيها
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Eye, Search } from "lucide-react";
import { groupService, getErrorMessage } from "../../services/groupService";
import StudentProgressDrawer from "./StudentProgressDrawer";
import {
  Avatar,
  EmptyState,
  Pagination,
  Spinner,
  fmt,
  formatRelative,
  recencyTone,
  useDebounce,
  useGroupStyles,
} from "./groupsShared";

const ORDERINGS = [
  ["name", "الاسم"],
  ["-score", "الأعلى نقاطاً"],
  ["score", "الأقل نقاطاً"],
  ["-activity", "الأحدث نشاطاً"],
  ["activity", "الأقدم نشاطاً"],
];

export default function MyStudents() {
  useGroupStyles();
  const [search, setSearch] = useState("");
  const q = useDebounce(search);
  const [ordering, setOrdering] = useState("name");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ count: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewing, setViewing] = useState(null); // { groupId, studentId }
  const reqRef = useRef(0);

  const load = useCallback(async () => {
    const id = ++reqRef.current;
    setLoading(true);
    setError("");
    try {
      const params = { page, page_size: 15, ordering };
      if (q.trim()) params.search = q.trim();
      const res = await groupService.listMyStudents(params);
      if (id !== reqRef.current) return;
      setRows(res.data);
      setMeta({ count: res.count, totalPages: res.total_pages });
    } catch (e) {
      if (id !== reqRef.current) return;
      if (e?.response?.status === 404 && page > 1) {
        setPage(1);
        return;
      }
      setError(getErrorMessage(e));
    } finally {
      if (id === reqRef.current) setLoading(false);
    }
  }, [page, q, ordering]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="gp-root">
      <div className="gp-head">
        <div>
          <Link to="/dashboard/groups" className="gp-btn gp-btn-sm" style={{ marginBottom: 14 }}>
            <ArrowRight size={14} /> المجموعات
          </Link>
          <h1 className="gp-title">كل طلابي</h1>
          <p className="gp-sub">
            كل الطلاب الموجودين في أي مجموعة عندك، من غير تكرار حتى لو الطالب في أكتر من مجموعة.
            {meta.count > 0 && ` الإجمالي: ${meta.count} طالب.`}
          </p>
        </div>
      </div>

      <div className="gp-toolbar">
        <div className="gp-search">
          <Search size={15} />
          <input
            className="gp-input"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="ابحث باسم الطالب أو بريده"
            aria-label="بحث في الطلاب"
          />
        </div>
        <select
          className="gp-select"
          value={ordering}
          onChange={(e) => {
            setOrdering(e.target.value);
            setPage(1);
          }}
          aria-label="ترتيب الطلاب"
        >
          {ORDERINGS.map(([v, label]) => (
            <option key={v} value={v}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {error && <div className="gp-error">{error}</div>}

      {loading && rows.length === 0 ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <div className="gp-panel">
          {q.trim() ? (
            <EmptyState title="لا توجد نتائج" hint="لا يوجد طالب بهذا الاسم أو البريد." />
          ) : (
            <EmptyState
              title="لا يوجد طلاب بعد"
              hint="أضف طلاباً إلى إحدى مجموعاتك وسيظهرون هنا."
              action={
                <Link className="gp-btn gp-btn-primary" to="/dashboard/groups">
                  الذهاب للمجموعات
                </Link>
              }
            />
          )}
        </div>
      ) : (
        <div className="gp-panel gp-table" aria-busy={loading}>
          <div className="gp-row mine head">
            <span>الطالب</span>
            <span className="gp-hide-sm">المجموعات</span>
            <span className="gp-hide-sm">النقاط</span>
            <span className="gp-hide-sm">آخر نشاط</span>
            <span />
          </div>
          {rows.map((s) => {
            const sm = s.progress?.summary;
            return (
              <div className="gp-row mine" key={s.id}>
                <div className="gp-who">
                  <Avatar name={s.full_name} src={s.profile_picture_url} />
                  <div style={{ minWidth: 0 }}>
                    <b>{s.full_name}</b>
                    <small>{s.email}</small>
                  </div>
                </div>
                <div className="gp-tags gp-hide-sm">
                  {s.groups.map((g) => (
                    <Link key={g.id} to={`/dashboard/groups/${g.id}`}>
                      {g.name}
                    </Link>
                  ))}
                </div>
                <span className="gp-num gp-hide-sm">{fmt(sm?.total_score)}</span>
                <span className={`gp-recency gp-hide-sm ${recencyTone(sm?.last_activity)}`}>
                  {formatRelative(sm?.last_activity)}
                </span>
                <div className="gp-row-actions">
                  <button
                    className="gp-icon-btn"
                    onClick={() => setViewing({ groupId: s.groups[0]?.id, studentId: s.id })}
                    disabled={!s.groups.length}
                    aria-label={`تقدم ${s.full_name}`}
                  >
                    <Eye size={15} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Pagination page={page} totalPages={meta.totalPages} onChange={setPage} />

      {viewing && (
        <StudentProgressDrawer groupId={viewing.groupId} studentId={viewing.studentId} onClose={() => setViewing(null)} />
      )}
    </div>
  );
}